import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, Database, Zap } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does SELECT do in a QUERY?',
    options: [
      'Filters which rows to include',
      'Chooses which columns to include',
      'Sorts the results',
      'Groups the results',
    ],
    correctIndex: 1,
    explanation:
      'SELECT specifies which columns to include in the output. SELECT A, C means "give me only columns A and C".',
  },
  {
    question: 'What does WHERE do?',
    options: [
      'Chooses which columns to include',
      'Sorts the data',
      'Filters which rows to include',
      'Renames the output columns',
    ],
    correctIndex: 2,
    explanation:
      'WHERE filters rows based on a condition. WHERE B=\'offer_123\' means "only include rows where column B is offer_123".',
  },
  {
    question: 'What does =QUERY(A1:C, "SELECT A, C WHERE B=\'offer_123\'") return from this data?',
    context: {
      headers: ['A: Date', 'B: Offer ID', 'C: Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '1,250'],
        ['2024-01-15', 'offer_456', '890'],
        ['2024-01-16', 'offer_123', '1,100'],
        ['2024-01-16', 'offer_456', '950'],
      ],
    },
    options: [
      'All columns for rows where B is offer_123',
      'Columns A and C for all rows',
      'Date and Clicks for the two offer_123 rows only',
      'Only column B where it equals offer_123',
    ],
    correctIndex: 2,
    explanation:
      'SELECT A, C chooses Date and Clicks. WHERE B=\'offer_123\' filters to only the two offer_123 rows. Result: 2024-01-15 / 1,250 and 2024-01-16 / 1,100.',
  },
  {
    question: "What is the advantage of QUERY over multiple VLOOKUPs?",
    options: [
      'QUERY is always faster',
      'QUERY uses less memory',
      'One formula replaces many, easier to maintain',
      'QUERY has better error handling',
    ],
    correctIndex: 2,
    explanation:
      'Instead of building 10 separate VLOOKUP formulas to assemble a report, one QUERY formula does it all. One place to update, one thing that can break.',
  },
  {
    question: 'How do you sort results in QUERY?',
    options: [
      'Use the SORT function around QUERY',
      'Add ORDER BY to the query string',
      'Use SORT BY in the query string',
      'Sorting is not possible in QUERY',
    ],
    correctIndex: 1,
    explanation:
      'ORDER BY is part of the QUERY language. For example: "SELECT A, B ORDER BY B DESC" sorts the results by column B in descending order.',
  },
  {
    question: 'What does GROUP BY do in a QUERY?',
    options: [
      'Groups columns side by side',
      'Combines rows with the same value and allows aggregate functions like SUM',
      'Sorts the results into groups alphabetically',
      'Hides duplicate rows from the output',
    ],
    correctIndex: 1,
    explanation:
      'GROUP BY combines rows that share the same value in a column, letting you use aggregate functions like SUM, COUNT, or AVG on the grouped data. For example, GROUP BY A with SUM(C) totals clicks per offer.',
  },
  {
    question: 'What does LABEL do in a QUERY?',
    options: [
      'Adds a label column to the data',
      'Filters rows that match a label',
      'Renames column headers in the output',
      'Groups data by label values',
    ],
    correctIndex: 2,
    explanation:
      'LABEL renames the column headers in the query output. For example, LABEL SUM(C) \'Total Clicks\' changes the default header "sum Clicks" to "Total Clicks".',
  },
  {
    question: 'What does "SELECT *" mean in a QUERY?',
    options: [
      'Select only starred/favourite columns',
      'Select all columns from the data range',
      'Select the first column only',
      'It causes an error — you must name columns',
    ],
    correctIndex: 1,
    explanation:
      'SELECT * means "select all columns". It returns every column in the data range without having to list them individually.',
  },
  {
    question: 'How do you sort in descending order in QUERY?',
    options: [
      'ORDER BY C DESCENDING',
      'ORDER BY C DESC',
      'SORT BY C DESC',
      'ORDER BY C REVERSE',
    ],
    correctIndex: 1,
    explanation:
      'Use DESC after the column name in ORDER BY. For example, "ORDER BY C DESC" sorts column C from highest to lowest. ASC (ascending) is the default.',
  },
  {
    question: 'What is the third parameter of the QUERY function used for?',
    options: [
      'Setting the output format',
      'Specifying the number of header rows in the data',
      'Limiting the number of results returned',
      'Choosing which sheet to query',
    ],
    correctIndex: 1,
    explanation:
      'The third parameter tells QUERY how many header rows your data has. Usually you set it to 1 so QUERY knows the first row contains column names, not data.',
  },
  {
    question: 'Which QUERY clause filters this table to show only rows with more than 1000 clicks?',
    context: {
      headers: ['A: Offer ID', 'B: Name', 'C: Clicks'],
      rows: [
        ['offer_101', 'CreditCards', '2,340'],
        ['offer_123', 'InsureMe', '980'],
        ['offer_145', 'TravelDeals', '3,100'],
        ['offer_167', 'SaveMore', '650'],
      ],
    },
    options: [
      'SELECT C > 1000',
      'WHERE C > 1000',
      'HAVING C > 1000',
      'LIMIT C > 1000',
    ],
    correctIndex: 1,
    explanation:
      'WHERE filters rows based on conditions. WHERE C > 1000 keeps only offer_101 (2,340) and offer_145 (3,100), excluding the two with fewer than 1000 clicks.',
  },
  {
    question: 'Which QUERY counts how many rows exist for each offer in this data?',
    context: {
      headers: ['A: Offer ID', 'B: Clicks'],
      rows: [
        ['offer_101', '2,340'],
        ['offer_123', '1,250'],
        ['offer_101', '1,890'],
        ['offer_123', '980'],
        ['offer_145', '3,100'],
      ],
    },
    options: [
      '"SELECT A, COUNT(A) GROUP BY A"',
      '"SELECT A, SUM(A) GROUP BY A"',
      '"SELECT COUNT(A) WHERE A"',
      '"SELECT A, TOTAL(A) GROUP BY A"',
    ],
    correctIndex: 0,
    explanation:
      'COUNT(A) counts the number of rows for each group. With GROUP BY A, the result would be: offer_101 = 2, offer_123 = 2, offer_145 = 1.',
  },
]

/* ─── Demo data ─── */
const DEMO_DATA = [
  { a: 'offer_101', b: 'CreditCards', c: 2340, d: 45 },
  { a: 'offer_123', b: 'InsureMe', c: 1250, d: 32 },
  { a: 'offer_101', b: 'CreditCards', c: 1890, d: 38 },
  { a: 'offer_145', b: 'TravelDeals', c: 3100, d: 67 },
  { a: 'offer_123', b: 'InsureMe', c: 980, d: 25 },
  { a: 'offer_145', b: 'TravelDeals', c: 2750, d: 59 },
]

const COL_LABELS = { a: 'A (Offer ID)', b: 'B (Name)', c: 'C (Clicks)', d: 'D (Conversions)' }

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Interactive QUERY visualiser ─── */
function QueryVisualiser() {
  const [activeClause, setActiveClause] = useState('select')

  const clauses = {
    select: {
      label: 'SELECT A, B, D',
      description: 'Chooses columns A, B, and D. Column C (Clicks) is excluded from the output.',
      color: 'blue',
      highlightCols: ['a', 'b', 'd'],
      filterRows: null,
    },
    where: {
      label: "WHERE A = 'offer_123'",
      description: 'Keeps only rows where column A is "offer_123". All other rows are excluded.',
      color: 'amber',
      highlightCols: ['a', 'b', 'd'],
      filterRows: (row) => row.a === 'offer_123',
    },
  }

  const active = clauses[activeClause]
  const colKeys = active.highlightCols
  const rows = active.filterRows ? DEMO_DATA.filter(active.filterRows) : DEMO_DATA

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-gray-900 p-4">
        <code className="text-sm font-mono">
          <span className="text-gray-500">=QUERY(A1:D100, "</span>
          <button
            onClick={() => setActiveClause('select')}
            className={`rounded px-1 transition-colors ${activeClause === 'select' ? 'bg-blue-900/50 text-blue-400 font-bold' : 'text-gray-400 hover:text-blue-300'}`}
          >
            SELECT A, B, D
          </button>
          <span className="text-gray-500"> </span>
          <button
            onClick={() => setActiveClause('where')}
            className={`rounded px-1 transition-colors ${activeClause === 'where' ? 'bg-amber-900/50 text-amber-400 font-bold' : 'text-gray-400 hover:text-amber-300'}`}
          >
            WHERE A = 'offer_123'
          </button>
          <span className="text-gray-500">")</span>
        </code>
      </div>

      <div className={`rounded-lg border px-4 py-3 ${activeClause === 'select' ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}`}>
        <p className={`text-sm font-semibold ${activeClause === 'select' ? 'text-blue-800' : 'text-amber-800'}`}>
          {active.label}
        </p>
        <p className={`text-sm ${activeClause === 'select' ? 'text-blue-700' : 'text-amber-700'}`}>
          {active.description}
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {colKeys.map((col) => (
                <th key={col} className="border-b border-r border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 last:border-r-0">
                  {COL_LABELS[col]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <AnimatePresence mode="wait">
              {rows.map((row, i) => (
                <motion.tr
                  key={`${activeClause}-${i}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {colKeys.map((col) => (
                    <td key={col} className="border-b border-r border-gray-200 px-3 py-1.5 text-xs text-gray-800 last:border-r-0">
                      {typeof row[col] === 'number' ? row[col].toLocaleString() : row[col]}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
        <div className={`text-center py-1.5 text-xs font-medium ${activeClause === 'select' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
          {rows.length} row{rows.length !== 1 ? 's' : ''} returned
        </div>
      </div>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module8() {
  return (
    <ModuleLayout moduleId={9}>
      <div className="space-y-8">
        {/* Section 1: What is QUERY? */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">What is QUERY?</h2>
              <p className="text-lg text-gray-600">
                QUERY lets you write database-style questions about your spreadsheet data. It is the most powerful function in Google Sheets.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-purple-400">QUERY</span>
                <span className="text-gray-500">(</span>
                <span className="text-green-300">data</span>
                <span className="text-gray-500">, </span>
                <span className="text-amber-300">query_string</span>
                <span className="text-gray-500">, [</span>
                <span className="text-blue-300">headers</span>
                <span className="text-gray-500">])</span>
              </code>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="bg-green-900/40 text-green-300 rounded px-2 py-0.5">data = the range to query</span>
                <span className="bg-amber-900/40 text-amber-300 rounded px-2 py-0.5">query_string = your question in query language</span>
                <span className="bg-blue-900/40 text-blue-300 rounded px-2 py-0.5">headers = number of header rows (usually 1)</span>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 rounded-xl border border-purple-200 bg-purple-50 p-5"
            >
              <Database className="w-8 h-8 text-purple-500 shrink-0" />
              <div>
                <p className="font-semibold text-purple-800">Think of it as SQL for spreadsheets</p>
                <p className="text-sm text-purple-600">
                  If you have ever written a database query, QUERY will feel familiar. If not, do not worry — it reads almost like English.
                </p>
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Section 2: SELECT and WHERE */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">SELECT and WHERE</h2>
              <p className="text-lg text-gray-600">
                The two most important keywords. SELECT chooses columns. WHERE filters rows.
              </p>
            </div>

            <QueryVisualiser />

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm text-emerald-800">
                <strong>Click each part of the formula above</strong> to see how SELECT and WHERE each affect the output. SELECT controls which columns appear. WHERE controls which rows appear.
              </p>
            </div>
          </div>
        </Section>

        {/* Section 3: More Query Powers */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">More Query Powers</h2>
              <p className="text-lg text-gray-600">
                QUERY can also sort, rename columns, and aggregate data.
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="rounded-xl border border-blue-200 bg-blue-50 p-5"
              >
                <h4 className="font-bold text-blue-800 mb-2">ORDER BY — Sort results</h4>
                <div className="rounded-lg bg-gray-900 px-3 py-2 mb-2">
                  <code className="text-sm text-blue-400 font-mono">=QUERY(A1:D, "SELECT * ORDER BY C DESC")</code>
                </div>
                <p className="text-sm text-blue-700">Sort by clicks, highest first. DESC = descending, ASC = ascending.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="rounded-xl border border-green-200 bg-green-50 p-5"
              >
                <h4 className="font-bold text-green-800 mb-2">LABEL — Rename columns</h4>
                <div className="rounded-lg bg-gray-900 px-3 py-2 mb-2">
                  <code className="text-sm text-green-400 font-mono">=QUERY(A1:D, "SELECT A, SUM(C) GROUP BY A LABEL SUM(C) 'Total Clicks'")</code>
                </div>
                <p className="text-sm text-green-700">Rename the output column header to something readable.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-xl border border-purple-200 bg-purple-50 p-5"
              >
                <h4 className="font-bold text-purple-800 mb-2">GROUP BY — Aggregate data</h4>
                <div className="rounded-lg bg-gray-900 px-3 py-2 mb-2">
                  <code className="text-sm text-purple-400 font-mono">=QUERY(A1:D, "SELECT A, SUM(C) GROUP BY A")</code>
                </div>
                <p className="text-sm text-purple-700">Total clicks per offer. Like a pivot table in one formula.</p>

                <div className="mt-3 overflow-hidden rounded-lg border border-purple-200">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="border-b border-r border-purple-200 px-3 py-1.5 text-xs text-purple-700 font-medium">Offer ID</th>
                        <th className="border-b border-purple-200 px-3 py-1.5 text-xs text-purple-700 font-medium">SUM(Clicks)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-r border-purple-100 px-3 py-1.5 font-mono text-xs">offer_101</td>
                        <td className="border-b border-purple-100 px-3 py-1.5 text-xs font-semibold">4,230</td>
                      </tr>
                      <tr>
                        <td className="border-b border-r border-purple-100 px-3 py-1.5 font-mono text-xs">offer_123</td>
                        <td className="border-b border-purple-100 px-3 py-1.5 text-xs font-semibold">2,230</td>
                      </tr>
                      <tr>
                        <td className="border-r border-purple-100 px-3 py-1.5 font-mono text-xs">offer_145</td>
                        <td className="px-3 py-1.5 text-xs font-semibold">5,850</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Section 4: QUERY vs Multiple VLOOKUPs */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">QUERY vs Multiple VLOOKUPs</h2>
              <p className="text-lg text-gray-600">
                Why learn QUERY when you already know VLOOKUP? Because one formula replaces ten.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="rounded-xl border-2 border-red-200 bg-red-50 p-5"
              >
                <h4 className="font-bold text-red-800 mb-3">10 VLOOKUP Formulas</h4>
                <div className="space-y-1.5">
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} className="rounded bg-gray-900 px-2 py-1">
                      <code className="text-[10px] text-red-400 font-mono">=VLOOKUP("offer_123", Data!A:D, {i + 2}, FALSE)</code>
                    </div>
                  ))}
                  <p className="text-xs text-red-500 italic text-center">...and 5 more for formatting, totals, etc.</p>
                </div>
                <ul className="mt-3 space-y-1 text-sm text-red-700">
                  <li>10 formulas to maintain</li>
                  <li>10 things that can break</li>
                  <li>10 places to update if the data changes</li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-bold text-emerald-800">1 QUERY Formula</h4>
                </div>
                <div className="rounded bg-gray-900 px-2 py-2 mb-3">
                  <code className="text-xs text-emerald-400 font-mono leading-relaxed">
                    =QUERY(Data!A:D,<br />
                    &nbsp;&nbsp;"SELECT A, B, C, D<br />
                    &nbsp;&nbsp;&nbsp;WHERE A='offer_123'<br />
                    &nbsp;&nbsp;&nbsp;ORDER BY B")
                  </code>
                </div>
                <ul className="space-y-1 text-sm text-emerald-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    One formula. One place to update.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    One thing that can break.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    Selects, filters, and sorts all at once.
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center"
            >
              <p className="text-emerald-800 font-semibold text-lg">
                One formula. One place to update. One thing that can break.
              </p>
              <p className="text-sm text-emerald-600 mt-1">
                This is why QUERY is worth learning.
              </p>
            </motion.div>
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={9} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
